'use client';

import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { PhotonGlobalSpotlight } from './MagicGlow';
import styles from './WavelengthsSection.module.css';

const RED_IMAGE = 'web_v2/photon/landing-page/wavelength-660nm.png';
const NIR_IMAGE = 'web_v2/photon/landing-page/wavelength-850nm.png';

/* Cinematic tissue cross-section. Image is naturally wide (3876×1551,
   ~2.5:1). Desktop renders at the natural aspect; mobile keeps the image
   height the same and crops the sides via object-fit: cover, so the
   section becomes taller while the imagery stays at its native scale. */
const MAP_BG_IMAGE = 'web_v2/photon/landing-page/penetration-map-bg.png';

const WavelengthsSection = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const t = useTranslations('photon');
  /* One-time reveal: cards + penetration map fade in and rise when the
     section enters the viewport. Header stays static. */
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <section className={styles.section} ref={sectionRef}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <h2 className={styles.heading}>
            {t('photon.wavelengthsSection.heading.twoWavelengths')}
            <br />
            {t('photon.wavelengthsSection.heading.twoPenetrationDepths')}
          </h2>
          <p className={styles.description}>
            {t('photon.wavelengthsSection.description.dualWavelength')}
          </p>
        </div>

        <div className={styles.cardGrid}>
          <div
            className={styles.card}
            data-magic-card
            data-revealed={revealed}
            style={{ ['--i' as string]: 0 } as CSSProperties}
          >
            <CustomImage
              className={styles.cardImage}
              src={RED_IMAGE}
              alt={t('photon.wavelengthsSection.image.visibleRedLight')}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            <div className={styles.cardContent}>
              <p className={styles.cardEyebrow}>
                {t('photon.wavelengthsSection.eyebrow.visibleRed')}
              </p>
              <p className={styles.cardWavelength}>
                <span className="value">660</span>
                <span className="unit">nm</span>
              </p>
              <p className={styles.cardBody}>
                {t('photon.wavelengthsSection.text.dermisSupport')}
              </p>
            </div>
          </div>
          <div
            className={styles.card}
            data-magic-card
            data-revealed={revealed}
            style={{ ['--i' as string]: 1 } as CSSProperties}
          >
            <CustomImage
              className={styles.cardImage}
              src={NIR_IMAGE}
              alt={t('photon.wavelengthsSection.image.nearInfraredLight')}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
            />
            <div className={styles.cardContent}>
              <p className={styles.cardEyebrow}>
                {t('photon.wavelengthsSection.eyebrow.nearInfrared')}
              </p>
              <p className={styles.cardWavelength}>
                <span className="value">850</span>
                <span className="unit">nm</span>
              </p>
              <p className={styles.cardBody}>
                {t('photon.wavelengthsSection.text.muscleJointSupport')}
              </p>
            </div>
          </div>

          <div
            className={styles.penetrationMap}
            data-magic-card
            aria-label={t(
              'photon.wavelengthsSection.ariaLabel.tissuePenetrationMap',
            )}
            data-revealed={revealed}
          >
            <CustomImage
              className={styles.mapBackgroundImage}
              src={MAP_BG_IMAGE}
              alt=""
              fill
              sizes="(max-width: 1320px) 100vw, 1232px"
            />
            <div className={styles.mapFade} data-side="left" aria-hidden="true" />
            <div
              className={styles.mapFade}
              data-side="right"
              aria-hidden="true"
            />
            <div className={styles.mobileImageDim} aria-hidden="true" />

            {/* Tissue layer labels — left column for the upper layers,
                right column for the deeper layers, each anchored at the
                edge with a small square indicator next to the text. */}
            <p
              className={styles.tissueLayerLabel}
              data-align="left"
              style={
                { ['--x']: '3.57%', ['--y']: '17.02%' } as CSSProperties
              }
            >
              {t('photon.wavelengthsSection.label.epidermis')}
              <span className={styles.marker} aria-hidden="true" />
            </p>
            <p
              className={styles.tissueLayerLabel}
              data-align="left"
              style={
                { ['--x']: '3.57%', ['--y']: '28.43%' } as CSSProperties
              }
            >
              {t('photon.wavelengthsSection.label.dermis')}
              <span className={styles.marker} aria-hidden="true" />
            </p>
            <p
              className={styles.tissueLayerLabel}
              data-align="right"
              style={
                { ['--x']: '3.57%', ['--y']: '53.39%' } as CSSProperties
              }
            >
              <span className={styles.marker} aria-hidden="true" />
              {t('photon.wavelengthsSection.label.subcutaneous')}
            </p>
            <p
              className={styles.tissueLayerLabel}
              data-align="right"
              style={
                { ['--x']: '3.57%', ['--y']: '68.86%' } as CSSProperties
              }
            >
              <span className={styles.marker} aria-hidden="true" />
              {t('photon.wavelengthsSection.label.muscleJoint')}
            </p>

            {/* Wavelength annotations — beam labels baked into the
                cinematic background image; these texts tag them.
                Right-side annotation shares the same right offset as
                the right-side tissue labels so their right edges align. */}
            <p
              className={styles.wavelengthAnnotation}
              data-right="false"
              style={
                {
                  ['--annotation-color']: '#ff0202',
                  ['--x']: '3.57%',
                  ['--y']: '36.75%',
                } as CSSProperties
              }
            >
              <span className="value">660</span>
              <span className="unit">nm</span>
            </p>
            <p
              className={styles.wavelengthAnnotation}
              data-right="true"
              style={
                {
                  ['--annotation-color']: '#a03575',
                  ['--x']: '3.57%',
                  ['--y']: '77.18%',
                } as CSSProperties
              }
            >
              <span className="value">850</span>
              <span className="unit">nm</span>
            </p>

            {/* Dashed dividers — first matches 660nm red, second matches
                850nm magenta. Edges fade into the section background via
                a mask-image gradient so the lines never butt against the
                masked image edges. */}
            <div
              className={styles.dashedDivider}
              data-tone="red"
              style={{ ['--top']: '46.42%' } as CSSProperties}
              aria-hidden="true"
            />
            <div
              className={styles.dashedDivider}
              data-tone="magenta"
              style={{ ['--top']: '86.27%' } as CSSProperties}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
      <PhotonGlobalSpotlight sectionRef={sectionRef} />
    </section>
  );
};

export default WavelengthsSection;
